import React, { ChangeEvent, useCallback, useId, useState } from 'react';
import {
  FileIcon,
  FileSpreadsheetIcon,
  FileTextIcon,
  FileTypeIcon,
  UploadIcon,
} from 'lucide-react';

import { cn } from '@/lib/utils';

import { FormControl, FormItem, useFormField } from '../ui/form';
import { Input } from '../ui/input';

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return bytes + ' bytes';
  else if (bytes < 1048576) return (bytes / 1024).toFixed(2) + ' KB';
  else return (bytes / 1048576).toFixed(2) + ' MB';
};

const getFileIcon = (fileType: string) => {
  if (fileType.includes('pdf')) {
    return <FileIcon className="w-12 h-12 text-red-500" />;
  } else if (fileType.includes('sheet') || fileType.includes('excel')) {
    return <FileSpreadsheetIcon className="w-12 h-12 text-green-500" />;
  } else if (fileType.includes('word') || fileType.includes('document')) {
    return <FileTextIcon className="w-12 h-12 text-blue-500" />;
  } else {
    return <FileTypeIcon className="w-12 h-12 text-gray-500" />;
  }
};

interface IFileInputProps {
  disabled?: boolean;
  className?: string;
  value?: File | null;
  onChange?: (file: File | null) => void;
  placeholder?: React.ReactNode;
  children?: React.ReactNode;
}

const FileInput = React.forwardRef<HTMLInputElement, IFileInputProps>(
  ({ children, className, value, onChange, placeholder, disabled }, ref) => {
    const id = useId();

    const [file, setFile] = useState<File | null>(value ?? null);

    const handleFileChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        setFile(e.target.files?.[0] ?? null);
        onChange?.(e.target.files?.[0] ?? null);
      },
      [onChange],
    );

    const { error } = useFormField();

    return (
      <FormItem className="grid w-full">
        <label
          htmlFor={`file-${id}`}
          className={cn(
            'border-2 border-dashed border-gray-300 rounded-lg p-6',
            'text-center cursor-pointer hover:border-gray-400 hover:bg-muted transition-all',
            error && 'border-destructive',
            value && 'border-gray-400 bg-muted hover:border-gray-500',
            className,
          )}
        >
          <FormControl>
            <>
              <Input
                ref={ref}
                id={`file-${id}`}
                type="file"
                className="hidden"
                onChange={handleFileChange}
                disabled={disabled}
              />
              {file ? (
                <div className={cn('flex items-center space-x-4 gap-4 h-full')}>
                  {getFileIcon(file.type)}

                  <div className="text-sm text-left space-y-1">
                    <p className="break-all text-wrap font-medium">
                      {file.name}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {file.size
                        ? formatFileSize(file.size)
                        : 'Tamanho desconhecido'}
                    </p>
                  </div>
                </div>
              ) : (
                <div
                  className={cn(
                    'flex flex-col items-center justify-center h-full',
                    className,
                  )}
                >
                  <div className="rounded-full bg-neutral-200 p-3 mb-2">
                    <UploadIcon className="h-7 w-7 text-neutral-900" />
                  </div>
                  <p className="text-gray-500">
                    {placeholder ?? 'Clique para adicionar um arquivo'}
                  </p>
                </div>
              )}
            </>
          </FormControl>
        </label>
        {children}
      </FormItem>
    );
  },
);

export default FileInput;
